import { createTrackingConfig, getLabels } from '../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';
import CustomDepositorSplitView from './CustomDepositorSplitView';

const depositorTrackingLabels = getLabels('Depositor');
const depositorTrackingBase = createTrackingConfig('Depositor');

export const depositorConfig = {
  entityName: 'Depositor',
  
  // Entity information for file attachments and audit logs
  entityTypeId: 9, // Depositor entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/customer',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      // No lookups for base customer entity currently
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/customer'
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%', // 30% of table width 
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle, // From app config: 'badge'
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline' 
                            // default: default table with cards
                            // cards: cards table
                            // board: board table
                            // timeline: timeline table
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth // From app config: '60%'
  },

  // Features configuration
  features: {
    fileAttachments: true, // Enable file attachment functionality for depositors
    masterEntityTypeId: 9, // Depositor master entity type ID for audit logs
  },

  // Custom Split View Panel configuration for Depositor module
  customSplitViewPanel: {
    enabled: true,
    component: CustomDepositorSplitView  // Use the custom Depositor split view component
  },
  
  // Custom handlers for Depositor-specific business logic
  customHandlers: {
    onSave: null, // Will be defined in depositorPage
    onDelete: null, // Will be defined in depositorPage
    onLoad: null, // For any special loading logic
    onFormOpen: null, // Will be defined in depositorPage
    
    // Depositor-specific actions
    onActivate: null, // Will handle depositor activation
    onDeactivate: null, // Will handle depositor deactivation
    
    // Board functionality (if timeline/board views are needed)
    onBoardDrop: async ({ item, fromGroup, toGroup }) => {
      if (toGroup === fromGroup) return;
      await fetch(`/customer/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: toGroup === 'active' }),
      });
      // Optionally refresh list afterward
    }
  },

  // Service configuration (reference only for generic service mapping)
  service: {
    name: 'depositorService',
    methods: {
      getAll: 'getAllDepositors',
      getById: 'getDepositorById',
      create: 'createDepositor',
      update: 'updateDepositor',
      delete: 'deleteDepositor',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'Depositor Management',
  description: 'Comprehensive depositor management system for depositor relationships and compliance tracking',
  icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',

  // Fields to remove from forms before submission (relations/computed)
  fieldsToRemove: [
    'createdBy', 'updatedBy', 'createdAt', 'updatedAt', 'createdById', 'updatedById', 'fileAttachments', 'fileAttachmentCount', 'complaints'
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'name',
      label: 'Depositor Name',
      type: 'text',
      required: true,
      placeholder: 'Enter depositor name',
      table: { header: 'Name', width: '220px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
        message: 'Name is required and must be 2-255 characters'
      }
    },
    {
      name: 'code',
      label: 'Depositor Code',
      type: 'text',
      placeholder: 'Enter depositor code (e.g., SAP code)',
      table: { header: 'Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 100
      }
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text',
      placeholder: 'Enter category (e.g., Pharmacy, Hospital, Warehouse)',
      table: { header: 'Category', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 255
      }
    },
    {
      name: 'contactEmail',
      label: 'Contact Email',
      type: 'text',
      placeholder: 'name@example.com',
      table: { header: 'Email', width: '200px', sortable: true },
      filterable: true,
      validation: {
        pattern: /^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/, // allow empty or valid email
        message: 'Email must be valid if provided'
      }
    },
    {
      name: 'contactPhone',
      label: 'Contact Phone',
      type: 'text',
      placeholder: '+30 210 1234567',
      table: { header: 'Phone', width: '160px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 50
      }
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      placeholder: 'Enter street address',
      table: { header: 'Address', width: '250px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 500
      }
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      placeholder: 'Enter city',
      table: { header: 'City', width: '150px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 100
      }
    },
    {
      name: 'postalCode',
      label: 'Postal Code',
      type: 'text',
      placeholder: 'Enter postal code',
      table: { header: 'Postal Code', width: '120px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 20
      }
    },
    {
      name: 'taxId',
      label: 'Tax ID (Α.Φ.Μ.)',
      type: 'text',
      placeholder: 'Enter tax identification number',
      table: { header: 'Tax ID', width: '120px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 50
      }
    },
    {
      name: 'taxOffice',
      label: 'Tax Office (Δ.Ο.Υ.)',
      type: 'text',
      placeholder: 'Enter tax office',
      table: { header: 'Tax Office', width: '150px', sortable: true },
      filterable: true,
      validation: {
        maxLength: 100
      }
    },
    {
      name: 'status',
      label: 'Active',
      type: 'switch',
      required: true,
      defaultValue: true,
      switchLabel: 'Active depositor',
      description: 'Marks whether the depositor is currently active',
      labelPosition: 'left',
      table: {
        header: 'Active',
        width: '100px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        required: true
      }
    },
    // File Attachments
    { 
      name: 'fileAttachments', 
      label: 'File(s)', 
      type: 'fileUpload',
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024, // 50MB
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: { 
        header: 'File(s)', 
        width: '50px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    }
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    return columns;
  },

  exportFileName: 'qms_depositors',

  // Backend field mappings (nested display fields if any)
  fieldMappings: {
    // none required for base depositor fields
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    status: [true, false], // Active depositors first
    name: 'asc' // Alphabetical by default
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'name', 'code', 'category', 'status'
      ]
    },
    {
      name: 'contact',
      label: 'Contact',
      icon: '☎️',
      fields: [
        'contactEmail', 'contactPhone'
      ]
    },
    {
      name: 'location',
      label: 'Location',
      icon: '📍',
      fields: [
        'address', 'city', 'postalCode'
      ]
    },
    {
      name: 'tax',
      label: 'Tax Information',
      icon: '📋',
      fields: [
        'taxId', 'taxOffice'
      ]
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Basic Information',
      icon: '📄',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['name', 'code', 'category'],
      fieldSpans: {
        'name': 'md:col-span-6',
        'code': 'md:col-span-3',
        'category': 'md:col-span-3'
      }
    },
    {
      title: 'Contact Information',
      icon: '☎️',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['contactEmail', 'contactPhone']
    },
    {
      title: 'Address Information',
      icon: '📍',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['address', 'city', 'postalCode'],
      fieldSpans: {
        'address': 'md:col-span-12',
        'city': 'md:col-span-6',
        'postalCode': 'md:col-span-6'
      }
    },
    {
      title: 'Tax Information',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['taxId', 'taxOffice']
    },
    {
      title: 'Status',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['status']
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
  ],

  // Tracking Section — show audit users by display name (user objects from API / selected row)
  trackingSection: {
    ...depositorTrackingBase,
    fields: depositorTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: depositorTrackingLabels.createdDateLabel,
            userEmptyText: depositorTrackingLabels.notSpecifiedText,
            dateEmptyText: depositorTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: depositorTrackingLabels.updatedDateLabel,
              userEmptyText: depositorTrackingLabels.notModifiedText,
              dateEmptyText: depositorTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Depositor labels + custom overrides)
  labels: {
    // Using predefined Depositor labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Depositor',
    plural: 'Depositors',
    pageTitle: 'Depositor Management',
    pageSubtitle: 'Comprehensive depositor management for depositor relationships and compliance',
    searchPlaceholder: 'Search depositors by name, code, category, or contact...',
    viewTitle: 'View Depositor',
    noSelectionText: 'Select a depositor to see details',
    
    // Action labels
    activateDepositor: 'Activate Depositor',
    deactivateDepositor: 'Deactivate Depositor',
    contactDepositor: 'Contact Depositor'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Ensure boolean for status
    if (transformed.status !== undefined) {
      if (transformed.status === '' || transformed.status === null) {
        delete transformed.status;
      } else if (typeof transformed.status === 'string') {
        transformed.status = transformed.status === 'true';
      } else {
        transformed.status = Boolean(transformed.status);
      }
    }

    // Convert date fields (if any)
    const dateFields = [];
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading depositors...',
    noData: 'No depositors found. Create your first depositor to get started.',
    loadError: 'Failed to load depositors',
    saveSuccess: 'Depositor saved successfully!',
    saveError: 'Failed to save depositor',
    deleteSuccess: 'Depositor deleted successfully!',
    deleteError: 'Failed to delete depositor',
    deleteConfirm: 'Are you sure you want to delete this depositor? This action cannot be undone and will remove all associated data.',
    activateSuccess: 'Depositor activated successfully!',
    deactivateSuccess: 'Depositor deactivated successfully!'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    name: { short: 'Official name of the depositor company or organization.' },
    code: { short: 'Internal depositor code or SAP code for identification.' },
    category: { short: 'Category or type of depositor (Pharmacy, Hospital, Warehouse, etc.).' },
    contactEmail: { short: 'Primary email address for contacting the depositor.' },
    contactPhone: { short: 'Primary phone number for contacting the depositor.' },
    address: { short: 'Street address of the depositor.' },
    city: { short: 'City where the depositor is located.' },
    postalCode: { short: 'Postal code of the depositor address.' },
    taxId: { short: 'Tax Identification Number (Α.Φ.Μ.) for the depositor.' },
    taxOffice: { short: 'Tax Office (Δ.Ο.Υ.) where the depositor is registered.' },
    status: { short: 'Whether the depositor is currently active and available for business.' },
    fileAttachments: { short: 'Supporting documents, contracts, certificates, or related files for this depositor.' }
  }
};

export default depositorConfig;

